/**
 * API 사용량 통계 서비스
 */

const API_BASE = process.env.REACT_APP_API_URL || 'https://api.ilouli.com';

const TOKEN_KEY = 'ilouli_token';

// 쿠키에서 토큰 가져오기
const getToken = () => {
  const cookies = document.cookie.split(';');
  for (let cookie of cookies) {
    const [name, value] = cookie.trim().split('=');
    if (name === TOKEN_KEY) {
      return decodeURIComponent(value);
    }
  }
  try {
    const backupToken = localStorage.getItem(`${TOKEN_KEY}_backup`);
    if (backupToken) return backupToken;
  } catch (e) {
    console.warn('Failed to get backup token:', e);
  }
  return null;
};

// 공통 헤더
const getHeaders = () => {
  const token = getToken();
  return {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` })
  };
};

/**
 * 통계 요약 조회
 */
export const getSummary = async () => {
  const response = await fetch(`${API_BASE}/api/admin/api-usage/summary`, {
    headers: getHeaders(),
    credentials: 'include'
  });
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  return response.json();
};

/**
 * 카테고리별 통계 조회
 */
export const getByCategory = async (days = 7) => {
  const response = await fetch(`${API_BASE}/api/admin/api-usage/by-category?days=${days}`, {
    headers: getHeaders(),
    credentials: 'include'
  });
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  return response.json();
};

/**
 * 엔드포인트별 통계 조회
 */
export const getByEndpoint = async (days = 7, category = null, limit = 20) => {
  let url = `${API_BASE}/api/admin/api-usage/by-endpoint?days=${days}&limit=${limit}`;
  if (category) url += `&category=${encodeURIComponent(category)}`;

  const response = await fetch(url, {
    headers: getHeaders(),
    credentials: 'include'
  });
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  return response.json();
};

/**
 * 일별 추이 조회
 */
export const getDailyTrend = async (days = 14, category = null) => {
  let url = `${API_BASE}/api/admin/api-usage/daily-trend?days=${days}`;
  if (category) url += `&category=${encodeURIComponent(category)}`;

  const response = await fetch(url, {
    headers: getHeaders(),
    credentials: 'include'
  });
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  return response.json();
};

/**
 * 비용 API 사용량 조회
 */
export const getCostlyUsage = async (days = 30) => {
  const response = await fetch(`${API_BASE}/api/admin/api-usage/costly?days=${days}`, {
    headers: getHeaders(),
    credentials: 'include'
  });
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  return response.json();
};

/**
 * 최근 API 호출 로그 조회
 */
export const getRecentLogs = async (limit = 50, category = null, status = null) => {
  let url = `${API_BASE}/api/admin/api-usage/recent-logs?limit=${limit}`;
  if (category) url += `&category=${encodeURIComponent(category)}`;
  if (status) url += `&status=${status}`;

  const response = await fetch(url, {
    headers: getHeaders(),
    credentials: 'include'
  });
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  return response.json();
};

/**
 * 에러 통계 조회
 */
export const getErrorStats = async (days = 7) => {
  const response = await fetch(`${API_BASE}/api/admin/api-usage/errors?days=${days}`, {
    headers: getHeaders(),
    credentials: 'include'
  });
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  return response.json();
};

/**
 * 카테고리 목록 조회
 */
export const getCategories = async () => {
  const response = await fetch(`${API_BASE}/api/admin/api-usage/categories`, {
    headers: getHeaders(),
    credentials: 'include'
  });
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  return response.json();
};

/**
 * 로그 정리 실행
 */
export const cleanupLogs = async (days = 30) => {
  const response = await fetch(`${API_BASE}/api/admin/api-usage/cleanup`, {
    method: 'POST',
    headers: getHeaders(),
    credentials: 'include',
    body: JSON.stringify({ days })
  });
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  return response.json();
};

export default {
  getSummary,
  getByCategory,
  getByEndpoint,
  getDailyTrend,
  getCostlyUsage,
  getRecentLogs,
  getErrorStats,
  getCategories,
  cleanupLogs
};
